<template>
  <content-subpage
    class="youtube-settings-content-subpage"
    :visible="visible"
    :titles="titles"
    @content-subpage-hidden="actionHidden"
  >
    <template #header-buttons>
      <fetch-data-button
        :kind="dataSaveButton.kind"
        :size="dataSaveButton.size"
        :icon-only="dataSaveButton.iconOnly"
        :completed="dataSaveButton.completed"
        :tip-position="dataSaveButton.tipPosition"
        :tip-alignment="dataSaveButton.tipAlignment"
        :error="dataSaveButton.error"
        :error-message="dataSaveButton.errorMessage"
        @reset-error-state="handleResetErrorState"
        @reset-completed-state="handleResetCompletedState"
        @click="handleUpdateSettings"
      >
        <svg-icon :name="dataSaveButton.iconName" kind="action" />
      </fetch-data-button>
    </template>
    <template #main-content>
      <media-component
        v-if="isValidUrl"
        ref="media"
        mediatype="youtube"
        :youtube-src="youTubeUrl"
        :cue-points="cuePoints"
        :is-inline="false"
        :caption="false"
        :strict-mode="strictModeLocal"
        @media-loaded="mediaLoaded"
      />
      <empty-placeholder v-else>
        <template #icon>
          <EmptyImage />
        </template>
        {{ t("youtube_settings_no_url_label") }}
      </empty-placeholder>
    </template>
    <template #sub-content>
      <overlay-loader v-if="isLoading" />
      <div v-else class="youtube-settings">
        <cv-form>
          <dl class="field-set input-cue">
            <h5 class="form-header">{{ t("youtube_settings_header_label") }}</h5>
            <dt class="field-label">{{ t("media_settings_cuepoints_label") }}</dt>
            <dd class="field-input">
              <ul class="actions">
                <li class="action">
                  <text-input
                    :invalid-message="error.startTimeMessage"
                    :value="cueStart"
                    @input="handleStartTime"
                  />
                  <cv-button
                    class="button start"
                    kind="tertiary"
                    size="small"
                    @click="captureStart"
                  >
                    {{ t("media_settings_cue_start") }}
                  </cv-button>
                </li>
                <li class="action">
                  <text-input
                    :invalid-message="error.endTimeMessage"
                    :value="cueEnd"
                    @input="handleEndTime"
                  />
                  <cv-button class="button end" kind="tertiary" size="small" @click="captureEnd">
                    {{ t("media_settings_cue_end") }}
                  </cv-button>
                </li>
              </ul>
              <input-checkbox
                id="checked-non-strict-mode"
                value="checked-non-strict-mode"
                :selected-values="nonStrictModeSelectedValues"
                @change="handleNonStrictModeChange"
              >
                {{ t("media_settings_non_strict_mode_settings") }}
              </input-checkbox>
            </dd>
          </dl>
          <dl class="field-set type-text url">
            <dt class="field-label">URL</dt>
            <dd class="field-input">
              <text-input
                :value="url"
                :invalid-message="error.urlMessage"
                :placeholder="urlPlaceholder"
                @input="handleUpdateUrl"
              />
            </dd>
          </dl>
        </cv-form>
      </div>
    </template>
  </content-subpage>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, defineAsyncComponent } from "vue";
import mediaUtils from "../media.utils";
import randomUtils from "@utils/random.utils";
import mediaEventBus from "@plugins/media/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import embedUtils from "@plugins/richtext/utils/embed.utils";
import TextInput from "@plugins/common/components/TextInput.vue";
import InputCheckbox from "@plugins/common/components/InputCheckbox.vue";
import EmptyPlaceholder from "@plugins/common/components/EmptyPlaceholder.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import ContentSubpage from "@plugins/content-subpage/components/ContentSubpage.vue";
import MediaComponent from "@plugins/media/components/MediaComponent.vue";
import { useContentSubpage } from "@plugins/content-subpage/composables/content-subpage.ts";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const EmptyImage = defineAsyncComponent(
  () => import("@images/placeholder/empty-states/EmptyImage.svg?component"),
);

interface Params {
  youTubeUrl?: string;
  cuePoints: { start?: number; end?: number };
  editorSaveCallback?: () => void;
  eventBusWatchInstances?: unknown[];
  strictMode?: boolean;
}

const props = withDefaults(defineProps<{ params?: Params }>(), {
  params: () => ({
    youTubeUrl: "",
    cuePoints: {},
    strictMode: false,
    eventBusWatchInstances: [],
  }),
});

const id = ref<string>(randomUtils.randomId());
const activityTitle = ref<string>("YouTube Settings");
const isLoading = ref<boolean>(true);
const youTubeUrl = ref<string>(props.params.youTubeUrl ?? "");
const cuePoints = ref<{ start?: number; end?: number }>({ ...props.params.cuePoints });
const isValidUrl = ref<boolean>(true);
const cueStart = ref<string>("00:00");
const cueEnd = ref<string>("00:00");
const settingsUrl = ref<string | undefined>(undefined);
const duration = ref<string>("00:00");
const urlPlaceholder = ref<string>("Enter URL");
const error = ref<{ startTimeMessage: string; endTimeMessage: string; urlMessage: string }>({
  startTimeMessage: "",
  endTimeMessage: "",
  urlMessage: "",
});
const strictModeLocal = ref<boolean>(!!props.params.strictMode);
const nonStrictModeSelectedValues = ref<string[]>([]);

interface MediaExpose {
  getCurrentTime: () => Promise<number>;
  updateStartTime: (sec: number) => void;
  updateEndTime: (sec: number) => void;
  getDuration: () => Promise<number>;
}
const media = ref<MediaExpose | null>(null);

const dirtyId = computed<string>(() => id.value);

const {
  visible,
  titles,
  actionHidden,
  dataSaveButton,
  setVisible,
  isComponentDirty,
  removeComponentDirty,
  actionHiddenCallBack,
  setDirty,
  removeDirty,
} = useContentSubpage({}, dirtyId);

const url = computed<string>(() => {
  if (settingsUrl.value === undefined && props.params.youTubeUrl) {
    return props.params.youTubeUrl;
  }
  if (settingsUrl.value === undefined) {
    return "";
  }
  return settingsUrl.value;
});

if (!strictModeLocal.value) {
  nonStrictModeSelectedValues.value.push("checked-non-strict-mode");
}

titles.value.push(activityTitle.value);

const endInit = Number(cuePoints.value.end);
const startInit = Number(cuePoints.value.start);

cueStart.value = Number.isFinite(startInit) ? mediaUtils.formatTime(startInit) : "00:00";
cueEnd.value = Number.isFinite(endInit) ? mediaUtils.formatTime(endInit) : duration.value;

function captureStart(e: Event) {
  e.preventDefault();
  setDirty(dirtyId.value);
  media.value?.getCurrentTime().then((response) => {
    cueStart.value = response === 0 ? "00:00" : mediaUtils.formatTime(response);
    if (validateStartCuePoint(cueStart.value)) {
      cuePoints.value.start = response;
      media.value?.updateStartTime(response);
    }
  });
}

function captureEnd(e: Event) {
  e.preventDefault();
  setDirty(dirtyId.value);
  media.value?.getCurrentTime().then((response) => {
    cueEnd.value = response === 0 ? "00:00" : mediaUtils.formatTime(response);
    if (validateEndCuePoint(cueEnd.value)) {
      cuePoints.value.end = response;
      media.value?.updateEndTime(response);
    }
  });
}

function handleUpdateUrl(value: string) {
  setDirty(dirtyId.value);
  const urlStr = value;
  settingsUrl.value = urlStr;
  isValidUrl.value = false;
  nextTick(() => {
    cueStart.value = "00:00";
    cueEnd.value = "00:00";
    cuePoints.value = { start: undefined, end: undefined };
    if (validateUrl(urlStr, false)) {
      isValidUrl.value = true;
      youTubeUrl.value = urlStr;
    }
  });
}

function handleUpdateSettings() {
  const youTubeUrlLocal = url.value;
  const startTime = convertTimeStringToSeconds(cueStart.value);
  const endTime = convertTimeStringToSeconds(cueEnd.value);
  if (
    validateUrl(youTubeUrlLocal, true) &&
    validateStartCuePoint(cueStart.value) &&
    validateEndCuePoint(cueEnd.value)
  ) {
    const settings = {
      youTubeUrl: youTubeUrlLocal,
      cuePoints: { start: startTime, end: endTime },
      updateCallback: props.params.editorSaveCallback,
      strictMode: strictModeLocal.value,
    };
    removeDirty(dirtyId.value);
    actionHidden();
    EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
    mediaEventBus.$emit("settingsUpdated", settings);
  }
}

function handleStartTime(value: string) {
  setDirty(dirtyId.value);
  const startTime = value;
  cueStart.value = startTime;
  if (!validateStartCuePoint(startTime) || !validateEndCuePoint(cueEnd.value)) {
    return;
  }
  const startSec = convertTimeStringToSeconds(startTime);
  cuePoints.value.start = startSec;
  media.value?.updateStartTime(startSec);
  const endSec = convertTimeStringToSeconds(cueEnd.value);
  cuePoints.value.end = endSec;
  media.value?.updateEndTime(endSec);
}

function handleEndTime(value: string) {
  setDirty(dirtyId.value);
  const endTime = value;
  cueEnd.value = endTime;
  if (!validateEndCuePoint(endTime) || !validateStartCuePoint(cueStart.value)) {
    return;
  }
  const endSec = convertTimeStringToSeconds(endTime);
  cuePoints.value.end = endSec;
  media.value?.updateEndTime(endSec);
  const startSec = convertTimeStringToSeconds(cueStart.value);
  cuePoints.value.start = startSec;
  media.value?.updateStartTime(startSec);
}

function validateStartCuePoint(cuePointStr: string): boolean {
  clearErrorState();
  const cuePointRegex = /^(\d+:)?([0-5]\d):([0-5]\d)$/;

  if (!cuePointRegex.test(cuePointStr)) {
    error.value.startTimeMessage = t("media_object_settings_time_format_invalid") as string;
    return false;
  }

  const cuePoint = convertTimeStringToSeconds(cuePointStr);
  const endTime = convertTimeStringToSeconds(cueEnd.value);
  if (cuePoint >= endTime) {
    error.value.startTimeMessage = t("media_object_settings_start_time_invalid") as string;
    return false;
  }

  const dur = convertTimeStringToSeconds(duration.value);
  if (cuePoint > dur) {
    error.value.startTimeMessage = t("youtube_settings_time_duration_invalid") as string;
    return false;
  }
  return true;
}

function validateEndCuePoint(cuePointStr: string): boolean {
  clearErrorState();
  const cuePointRegex = /^(\d+:)?([0-5]\d):([0-5]\d)$/;

  if (!cuePointRegex.test(cuePointStr)) {
    error.value.endTimeMessage = t("media_object_settings_time_format_invalid") as string;
    return false;
  }

  const cuePoint = convertTimeStringToSeconds(cuePointStr);
  const startTime = convertTimeStringToSeconds(cueStart.value);
  if (cuePoint <= startTime) {
    error.value.endTimeMessage = t("media_object_settings_end_time_invalid") as string;
    return false;
  }

  const dur = convertTimeStringToSeconds(duration.value);
  if (cuePoint > dur) {
    error.value.endTimeMessage = t("youtube_settings_time_duration_invalid") as string;
    return false;
  }
  return true;
}

function clearErrorState() {
  error.value = {
    startTimeMessage: "",
    endTimeMessage: "",
    urlMessage: "",
  };
}

function validateUrl(youtubeUrl: string, showErrorMsg: boolean): boolean {
  clearErrorState();
  if (youtubeUrl.length === 0) {
    isValidUrl.value = false;
    settingsUrl.value = "";
    if (showErrorMsg) {
      error.value.urlMessage = t("youtube_key_in_url_message") as string;
    }
    return false;
  } else {
    const urlWithProtocol = prependProtocol(youtubeUrl);
    const isValid = embedUtils.isValidUrl(urlWithProtocol, undefined);
    const hostName = isValid ? new URL(urlWithProtocol).hostname : undefined;
    const validDomainPattern = ["youtube.com", "youtube-nocookie.com", "youtu.be", "m.youtube.com"];
    const validUrl = isValid && !!hostName && validDomainPattern.some((v) => hostName.includes(v));
    if (validUrl) {
      return true;
    } else {
      if (showErrorMsg) {
        error.value.urlMessage = t("youtube_key_in_valid_url_message") as string;
      }
      return false;
    }
  }
}

function convertTimeStringToSeconds(timeStr: string): number {
  const timeRegex = /^(\d+:)?([0-5]\d):([0-5]\d)$/;
  if (!timeRegex.test(timeStr)) {
    return 0;
  }
  return mediaUtils.deformatTime(timeStr);
}

function prependProtocol(youtubeUrl: string): string {
  const protocol = "https://";
  return youtubeUrl.includes(protocol) ? youtubeUrl : protocol + youtubeUrl;
}

function mediaLoaded() {
  const initialYouTubeUrl = props.params.youTubeUrl ?? "";
  const currentYouTubeUrl = url.value;

  const endCuePointSeconds = cuePoints.value.end;
  const hasEndCuePoint = Number.isFinite(Number(endCuePointSeconds));

  const durationPromise = media.value?.getDuration?.();
  if (!durationPromise) {
    isLoading.value = false;
    return;
  }

  durationPromise.then((rawDurationSeconds) => {
    const totalDurationSeconds = Number(rawDurationSeconds);
    const totalDurationFormatted = mediaUtils.formatTime(totalDurationSeconds);
    duration.value = totalDurationFormatted;

    if (hasEndCuePoint && initialYouTubeUrl === currentYouTubeUrl) {
      if (totalDurationSeconds > 0) {
        const endSeconds = Number(endCuePointSeconds);
        cueEnd.value =
          endSeconds > totalDurationSeconds
            ? totalDurationFormatted
            : mediaUtils.formatTime(endSeconds);
      }
    } else if (!hasEndCuePoint && totalDurationSeconds > 0) {
      cueEnd.value = totalDurationFormatted;
    }

    isLoading.value = false;
  });
}

function handleResetErrorState() {
  dataSaveButton.value.error = false;
}

function handleResetCompletedState() {
  dataSaveButton.value.completed = false;
}

function handleNonStrictModeChange(value: string[] = []) {
  setDirty(dirtyId.value);
  nonStrictModeSelectedValues.value = value;
  strictModeLocal.value = !nonStrictModeSelectedValues.value.includes("checked-non-strict-mode");
}

defineExpose({
  params: props.params,
  setVisible,
  isComponentDirty,
  removeComponentDirty,
  actionHiddenCallBack,
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.bx--modal.youtube-settings-content-subpage {
  .bx-form {
    margin-top: 0;
  }

  .empty-placeholder {
    background-color: $ui-01;
    border-radius: 10px;
  }

  .field-set {
    &.input-cue {
      .actions {
        display: flex;
        gap: $spacing-05;
      }
      .action {
        flex: 1 1 50%;

        .bx--form-item {
          margin-bottom: $spacing-03;
        }

        .bx--btn {
          width: 100%;
          margin-bottom: $spacing-01;
        }
      }
    }
  }

  .bx--text-input,
  .bx--text-area {
    background-color: $ui-background;
  }
}
</style>
